import { cn } from '../../../utils/cn';
import {
  LOAD_TYPE_LABELS,
  type CreateLoadPayload,
  type NurseryDeliveryZone,
  type NurseryLoadType,
} from '../../../types/nursery';

/**
 * A load's details — type, despatch farm and date, the zones a run serves,
 * vehicle / driver / carrier, capacity. Shared by the new-load dialog and the
 * load page's edit form. Controlled.
 */

export type LoadFieldsValue = Omit<CreateLoadPayload, 'order_ids'>;

export interface LoadFieldsProps {
  value: LoadFieldsValue;
  onChange: (value: LoadFieldsValue) => void;
  farms: { id: number; name: string; picker_count?: number }[];
  zones: NurseryDeliveryZone[];
  /** The type is fixed once a load exists. */
  typeLocked?: boolean;
}

const TYPES: NurseryLoadType[] = ['run', 'carrier', 'post', 'collection', 'ad_hoc', 'cross_dock'];

const inputCls =
  'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export function LoadFields({ value, onChange, farms, zones, typeLocked }: LoadFieldsProps) {
  const set = (patch: Partial<LoadFieldsValue>) => onChange({ ...value, ...patch });
  const farm = farms.find((f) => f.id === value.despatch_farm_id);
  const zoneIds = value.zone_ids ?? [];

  return (
    <div className="space-y-4">
      {!typeLocked && (
        <div>
          <span className="block text-sm font-medium text-neutral-700 mb-1">Kind of load</span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Kind of load">
            {TYPES.map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={value.load_type === t}
                onClick={() => set({ load_type: t })}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm',
                  value.load_type === t
                    ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium'
                    : 'bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50',
                )}
              >
                {LOAD_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="load-farm" className="block text-sm font-medium text-neutral-700 mb-1">Despatch farm</label>
          <select
            id="load-farm"
            value={value.despatch_farm_id || ''}
            onChange={(e) => set({ despatch_farm_id: Number(e.target.value) })}
            className={inputCls}
          >
            <option value="" disabled>Choose a farm…</option>
            {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          {farm && farm.picker_count === 0 && (
            <p className="text-xs text-amber-700 mt-1">Nobody is assigned to pick at this farm yet.</p>
          )}
        </div>
        <div>
          <label htmlFor="load-date" className="block text-sm font-medium text-neutral-700 mb-1">Despatch date</label>
          <input
            id="load-date"
            type="date"
            value={value.despatch_date}
            onChange={(e) => set({ despatch_date: e.target.value })}
            className={inputCls}
          />
        </div>
      </div>

      {value.load_type === 'run' && zones.length > 0 && (
        <div>
          <span className="block text-sm font-medium text-neutral-700 mb-1">Zones this run serves</span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Zones this run serves">
            {zones.map((z) => {
              const on = zoneIds.includes(z.id);
              return (
                <button
                  key={z.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => set({ zone_ids: on ? zoneIds.filter((id) => id !== z.id) : [...zoneIds, z.id] })}
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm max-w-full break-words text-left',
                    on ? 'bg-primary-50 border-primary-300 text-primary-800' : 'bg-white border-neutral-300 text-neutral-600',
                  )}
                >
                  {z.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {value.load_type === 'carrier' ? (
          <>
            <div>
              <label htmlFor="load-carrier" className="block text-sm font-medium text-neutral-700 mb-1">Carrier</label>
              <input id="load-carrier" value={value.carrier_name ?? ''} onChange={(e) => set({ carrier_name: e.target.value })}
                placeholder="e.g. Toll, Mainfreight" className={inputCls} />
            </div>
            <div>
              <label htmlFor="load-consignment" className="block text-sm font-medium text-neutral-700 mb-1">Consignment reference</label>
              <input id="load-consignment" value={value.consignment_ref ?? ''} onChange={(e) => set({ consignment_ref: e.target.value })}
                className={inputCls} />
            </div>
          </>
        ) : value.load_type === 'post' ? (
          <div className="sm:col-span-2">
            <label htmlFor="load-consignment" className="block text-sm font-medium text-neutral-700 mb-1">Lodgement / manifest reference</label>
            <input id="load-consignment" value={value.consignment_ref ?? ''} onChange={(e) => set({ consignment_ref: e.target.value })}
              className={inputCls} />
          </div>
        ) : value.load_type !== 'collection' ? (
          <>
            <div>
              <label htmlFor="load-vehicle" className="block text-sm font-medium text-neutral-700 mb-1">Vehicle</label>
              <input id="load-vehicle" value={value.vehicle ?? ''} onChange={(e) => set({ vehicle: e.target.value })}
                placeholder="e.g. Isuzu tray, 1ABC123" className={inputCls} />
            </div>
            <div>
              <label htmlFor="load-driver" className="block text-sm font-medium text-neutral-700 mb-1">Driver</label>
              <input id="load-driver" value={value.driver_name ?? ''} onChange={(e) => set({ driver_name: e.target.value })}
                className={inputCls} />
            </div>
          </>
        ) : null}
        <div>
          <label htmlFor="load-capacity" className="block text-sm font-medium text-neutral-700 mb-1">Capacity (trees, optional)</label>
          <input
            id="load-capacity"
            type="number"
            min={1}
            value={value.capacity_trees ?? ''}
            onChange={(e) => set({ capacity_trees: e.target.value ? Number(e.target.value) : null })}
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label htmlFor="load-notes" className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
        <textarea id="load-notes" rows={2} value={value.notes ?? ''} onChange={(e) => set({ notes: e.target.value })} className={inputCls} />
      </div>
    </div>
  );
}

export default LoadFields;
